import { useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import {
  animationFrameScheduler,
  defaultIfEmpty,
  filter,
  finalize,
  fromEvent,
  last,
  map,
  observeOn,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { Vector2, Vector3 } from 'three';

import { useSubscriptions } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  useEditingLabelBoxStore,
  useLabelingBoxStore,
  useToolMainCameraStore,
  useToolMenuStore,
} from '@/store';
import type { NEW__LabelingBox } from '@/util';
import { filterOrRestart, subVector, withCanvasXY, withTarget } from '@/util';

import {
  applyMoveOffsetToBox,
  getIntersectPlanePointByNDC,
  isEnabled,
  isFirstIntersectedBoxEqaul,
  isIntersected,
  isOtherOperationRunning,
  isSelectedLabelingBoxList,
  isTopMode,
  setControlEnabled,
  validMouseEventAndCanvasElement,
  withIntersectObject,
  withIntersectPlanePoint,
} from '../util';

const useDefaultViewTopMultiMove = ({
  setRenderBoxListById,
  disabled,
  isMultiMoveRef,
  isSingleMoveRef,
}: {
  setRenderBoxListById: (boxIdList: string[]) => void;
  disabled: boolean;
  isMultiMoveRef: React.RefObject<boolean>;
  isSingleMoveRef: React.RefObject<boolean>;
}) => {
  const { getMode } = useToolMainCameraStore();
  const { camera, controls } = useThree();
  const { labelingBoxList, selectedLabelingBoxList } = useLabelingBoxContext();
  const labelingBoxListRef = useRef<NEW__LabelingBox[]>([]);
  const selectedLabelingBoxListRef = useRef<NEW__LabelingBox[]>([]);
  const { isSelectedByToolName } = useToolMenuStore();
  const isAiAssistRunning = isSelectedByToolName('aiAssist');

  const prepareMultiMove = () => {
    setControlEnabled(false, controls);

    setRenderBoxListById(selectedLabelingBoxListRef.current.map(v => v.id));
    isMultiMoveRef.current = true;
    isSingleMoveRef.current = false;
  };

  const cleanupMultiMove = () => {
    useEditingLabelBoxStore.getState().clear();
    setControlEnabled(true, controls);
    setRenderBoxListById([]);
    isMultiMoveRef.current = false;
  };

  const cleanupOnInvalidStream = () => {
    isMultiMoveRef.current = false;
    if (isOtherOperationRunning(isSingleMoveRef.current)) return;

    useEditingLabelBoxStore.getState().clear();
    setRenderBoxListById([]);
    !isOtherOperationRunning(isAiAssistRunning) &&
      setControlEnabled(true, controls);
  };

  useEffect(() => {
    labelingBoxListRef.current = labelingBoxList;
  }, [labelingBoxList]);

  useSubscriptions(
    () => [
      fromEvent<PointerEvent>(window, 'pointerdown')
        .pipe(
          filter(() => isEnabled(disabled)),
          filter(() => isTopMode(getMode())),
          filter(() => !isOtherOperationRunning(isAiAssistRunning)),
          filter(
            () =>
              !useEditingLabelBoxStore.getState().isTransforming &&
              !useEditingLabelBoxStore.getState().isResizing,
          ),
          filter(() =>
            isSelectedLabelingBoxList(selectedLabelingBoxListRef.current),
          ),
          filter(
            e =>
              validMouseEventAndCanvasElement(e) ||
              (e.target instanceof HTMLDivElement &&
                !!e.target.ariaLabel?.match(/box-track-id/)),
          ),
          withTarget,
          withCanvasXY,
          map(e =>
            withIntersectObject(camera, selectedLabelingBoxListRef.current)(e),
          ),
          filterOrRestart(({ intersections }) => isIntersected(intersections)),
          filter(({ intersections: selectedIntersections, event }) => {
            const getIntersectLabelingBoxList = withIntersectObject(
              camera,
              labelingBoxListRef.current,
            );
            const { intersections } = getIntersectLabelingBoxList(event);

            return isFirstIntersectedBoxEqaul(
              intersections,
              selectedIntersections,
            );
          }),
          map(({ intersections, event }) => {
            const mouseDownPosition = withIntersectPlanePoint({
              event,
              camera,
              coplanarPoint: intersections[0].point,
            });
            return { intersections, event, mouseDownPosition };
          }),
          tap(prepareMultiMove),
          switchMap(
            ({ intersections: mouseDownIntersections, mouseDownPosition }) =>
              fromEvent<MouseEvent>(window, 'mousemove').pipe(
                withTarget,
                filterOrRestart(e => e.target instanceof HTMLCanvasElement),
                withCanvasXY,
                map(mouseMoveEvent => {
                  const mouseMovePosition = getIntersectPlanePointByNDC({
                    ndc: new Vector2(
                      mouseMoveEvent.canvasX,
                      mouseMoveEvent.canvasY,
                    ),
                    camera,
                    coplanarPoint: mouseDownIntersections[0].point,
                  });

                  return { mouseMovePosition };
                }),
                map(({ mouseMovePosition }) => {
                  const moveOffset = subVector(
                    mouseMovePosition,
                    mouseDownPosition,
                  );

                  return { moveOffset, mouseDownIntersections };
                }),
                filterOrRestart(
                  ({ moveOffset }) =>
                    !isNaN(moveOffset.x) && !isNaN(moveOffset.y),
                ),
                tap(({ moveOffset }) => {
                  useEditingLabelBoxStore
                    .getState()
                    .setMoveOffset([moveOffset.x, moveOffset.y, 0]);
                }),
                takeUntil(fromEvent<MouseEvent>(window, 'mouseup')),
                defaultIfEmpty({ moveOffset: new Vector3() }),
                last(),
                map(({ moveOffset }) => {
                  useLabelingBoxStore.getState().moveBoxList(
                    selectedLabelingBoxListRef.current.map(box =>
                      applyMoveOffsetToBox(moveOffset, {
                        x: box.position[0],
                        y: box.position[1],
                        z: box.position[2],
                      })(box.id),
                    ),
                  );

                  return selectedLabelingBoxListRef.current;
                }),
                observeOn(animationFrameScheduler),
                finalize(cleanupMultiMove),
              ),
          ),
          finalize(cleanupOnInvalidStream),
        )
        .subscribe(),
    ],
    [camera, controls, disabled, isAiAssistRunning],
  );

  useEffect(() => {
    selectedLabelingBoxListRef.current = selectedLabelingBoxList;
  }, [selectedLabelingBoxList]);
};

export default useDefaultViewTopMultiMove;
